<template>
<div>
  <div class="header-section">
    <div class="content-section">
      <h3 class="section-title">{{ $t('Reports') }}</h3>
      
      <!-- Loading Spinner -->
      <LoadingSpinner v-if="loading" />

      <div v-else>
        <div class="flex space-x-4 rtl:space-x-reverse border-b">
          <button 
            v-for="tab in tabs" 
            :key="tab.id"
            @click="switchTab(tab.id)"
            :class="[
              'px-4 py-2 text-sm font-medium',
              currentTab === tab.id
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            {{ tab.name }}
          </button>
        </div>

        <!-- Error Logs Table -->
        <div v-if="currentTab === 'errors'" class="bg-white mt-4">
          <div class="flex justify-between items-center mb-4">
            <button 
              @click="clearLogs" 
              class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              :disabled="loading"
            >
              {{ $t('reports.clearLogs') }}
            </button>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full border-collapse">
              <thead>
                <tr class="bg-gray-100">
                  <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('reports.time') }}</th>
                  <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('reports.method') }}</th>
                  <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('reports.statusCode') }}</th>
                  <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('reports.message') }}</th>
                  <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('reports.context') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="log in errorLogs" :key="log.timestamp" class="border-b hover:bg-gray-50">
                  <td class="p-3 text-base text-[#0D0D0D] w-44">{{ formatDate(log.timestamp) }}</td>
                  <td class="p-3 text-base text-[#0D0D0D]">{{ getMethod(log.context) }}</td>
                  <td class="p-3 text-base text-[#0D0D0D]">
                    <span v-if="getStatusCode(log.context)" class="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                      {{ getStatusCode(log.context) }}
                    </span>
                  </td>
               
                  <td class="p-3 text-base text-[#0D0D0D]">{{ log.message }}</td>
                  <td class="p-3 text-base text-[#0D0D0D]">{{ getContextMessage(log.context) }}</td>
                </tr>
                <tr v-if="errorLogs.length === 0">
                  <td colspan="7" class="p-3 text-center text-base text-[#0D0D0D]">
                    {{ $t('reports.noLogs') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- Pagination -->
          <div class="flex justify-between items-center mt-4">
            <div class="flex items-center space-x-2 rtl:space-x-reverse gap-2">
              <select v-model="pageSize" @change="fetchData(1)" class="border rounded px-2 py-1">
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              <span class="text-sm text-gray-500">{{ $t('reports.itemsPerPage') }}</span>
            </div>
            <div class="flex space-x-2 rtl:space-x-reverse">
              <button 
                @click="currentPage > 1 && fetchData(currentPage - 1)"
                :disabled="currentPage === 1"
                class="px-3 py-1 border rounded"
                :class="{ 'opacity-50 cursor-not-allowed': currentPage === 1 }"
              >
                {{ $t('common.previous') }}
              </button>
              <span class="px-3 py-1">{{ currentPage }}</span>
              <button 
                @click="fetchData(currentPage + 1)"
                class="px-3 py-1 border rounded"
              >
                {{ $t('common.next') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Received Messages Table -->
        <div v-if="currentTab === 'receive/live'" class="bg-white mt-4">
          <div>
            <div class="overflow-x-auto">
              <table class="w-full border-collapse mt-4">
                <thead>
                  <tr class="bg-gray-100">
                    <th class="p-3 text-right w-1/4 text-[#4D4D4D] text-sm font-light">{{ $t('reports.senderNumber') }}</th>
                    <th class="p-3 text-right w-1/4 text-[#4D4D4D] text-sm font-light">{{ $t('reports.messageText') }}</th>
                    <th class="p-3 text-right w-1/4 text-[#4D4D4D] text-sm font-light">{{ $t('reports.receiverLine') }}</th>
                    <th class="p-3 text-right w-1/4 text-[#4D4D4D] text-sm font-light">{{ $t('reports.receivedTime') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="message in receivedMessages" :key="message.mobile" class="border-b hover:bg-gray-50">
                    <td class="p-3 text-base text-[#0D0D0D]">{{ message.mobile }}</td>
                    <td class="p-3 text-base text-[#0D0D0D]">{{ message.messageText }}</td>
                    <td class="p-3 text-base text-[#0D0D0D]">{{ message.number }}</td>
                    <td class="p-3 text-base text-[#0D0D0D]">{{ new Date(message.receivedDateTime * 1000).toLocaleString('fa-IR') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Pagination -->
            <div class="flex justify-between items-center mt-4">
              <div class="flex items-center space-x-2 rtl:space-x-reverse gap-2">
                <select v-model="pageSize" class="border rounded px-2 py-1">
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span class="text-sm text-gray-500">{{ $t('reports.itemsPerPage') }}</span>
              </div>
              <div class="flex space-x-2 rtl:space-x-reverse">
                <button 
                  @click="currentPage > 1 && fetchData()"
                  :disabled="currentPage === 1"
                  class="px-3 py-1 border rounded"
                  :class="{ 'opacity-50 cursor-not-allowed': currentPage === 1 }"
                >
                  {{ $t('common.previous') }}
                </button>
                <span class="px-3 py-1">{{ currentPage }}</span>
                <button 
                  @click="fetchData(currentPage + 1)"
                  class="px-3 py-1 border rounded"
                >
                  {{ $t('common.next') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Sent Messages Table -->
        <div v-if="currentTab === 'send/live'" class="bg-white mt-4">
          <div>
            <div class="overflow-x-auto">
              <table class="w-full border-collapse mt-4">
                <thead>
                  <tr class="bg-gray-100">
                    <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('reports.messageId') }}</th>
                    <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('reports.recipientNumber') }}</th>
                    <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('reports.messageText') }}</th>
                    <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('reports.sendTime') }}</th>
                    <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('reports.lineNumber') }}</th>
                    <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('reports.cost') }}</th>
                    <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('reports.status') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="message in sentMessages" :key="message.messageId" class="border-b hover:bg-gray-50">
                    <td class="p-3 text-base text-[#0D0D0D]">{{ message.messageId }}</td>
                    <td class="p-3 text-base text-[#0D0D0D]">{{ message.mobile }}</td>
                    <td class="p-3 text-base text-[#0D0D0D]">{{ message.messageText }}</td>
                    <td class="p-3 text-base text-[#0D0D0D]">{{ new Date(message.sendDateTime * 1000).toLocaleString('fa-IR') }}</td>
                    <td class="p-3 text-base text-[#0D0D0D]">{{ message.lineNumber }}</td>
                    <td class="p-3 text-base text-[#0D0D0D]">{{ formatNumber(message.cost) }}</td>
                    <td class="p-3 text-base text-[#0D0D0D]">{{ getDeliveryStatus(message.deliveryState) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Pagination -->
            <div class="flex justify-between items-center mt-4">
              <div class="flex items-center space-x-2 rtl:space-x-reverse gap-2">
                <select v-model="pageSize" class="border rounded px-2 py-1">
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span class="text-sm text-gray-500">{{ $t('reports.itemsPerPage') }}</span>
              </div>
              <div class="flex space-x-2 rtl:space-x-reverse">
                <button 
                  @click="currentPage > 1 && fetchData()"
                  :disabled="currentPage === 1"
                  class="px-3 py-1 border rounded"
                  :class="{ 'opacity-50 cursor-not-allowed': currentPage === 1 }"
                >
                  {{ $t('common.previous') }}
                </button>
                <span class="px-3 py-1">{{ currentPage }}</span>
                <button 
                  @click="fetchData(currentPage + 1)"
                  class="px-3 py-1 border rounded"
                >
                  {{ $t('common.next') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import axios from 'axios'
import LoadingSpinner from '../LoadingSpinner.vue'

export default {
  name: 'ErrorReports',
  components: {
    LoadingSpinner
  },
  data() {
    return {
      currentTab: 'send/live',
      receivedMessages: [],
      sentMessages: [],
      errorLogs: [],
      currentPage: 1,
      pageSize: 20,
      loading: true,
      error: null,
      tabs: [
        { id: 'send/live', name: 'پیام‌های ارسالی' },
        { id: 'receive/live', name: 'پیام‌های دریافتی' },
        { id: 'errors', name: 'گزارش خطاها' }
      ]
    }
  },
  methods: {
    switchTab(tabId) {
      this.currentTab = tabId;
      this.currentPage = 1;
      this.updateUrl();
      this.fetchData(1);
    },
    updateUrl() {
      const url = new URL(window.location.href);
      url.searchParams.set('section', this.currentTab);
      window.history.pushState({}, '', url);
    },
    getTabFromUrl() {
      const url = new URL(window.location.href);
      const section = url.searchParams.get('section');
      return this.tabs.find(t => t.id === section) ? section : 'send/live';
    },
    async fetchData(page = 1) {
      this.loading = true;
      this.error = null;
      
      try {
        if (this.currentTab === 'errors') {
          console.log('Fetching error logs...');
          const response = await axios.post(ajaxurl, new URLSearchParams({
            action: 'sms_ir_get_error_logs',
            page: page,
            per_page: this.pageSize
          }));

          console.log('Error logs response:', response.data);
          
          if (response.data.success) {
            this.errorLogs = response.data.data.logs || [];
            console.log('Logs loaded:', this.errorLogs);
            console.log('Total logs:', this.errorLogs.length);
          } else {
            this.error = response.data.data || this.$t('error.fetchFailed');
            console.error('Failed to fetch logs:', this.error);
          }
        } else {
          const params = new URLSearchParams({
            action: "sms_ir_get_messages",
            type: this.currentTab,
            page: page,
            pageSize: this.pageSize
          });

          const response = await axios.get(
            `${window.sms_ir_login.ajaxUrl}?${params.toString()}`
          );

          if (response.data.success) {
            if (this.currentTab === 'receive/live') {
              this.receivedMessages = response.data.data;
            } else {
              this.sentMessages = response.data.data;
            }
            this.currentPage = page;
          } else {
            this.error = response.data.data || this.$t('error.fetchFailed');
          }
        }
      } catch (err) {
        console.error('Error in fetchData:', err);
        this.error = err.response?.data?.message || this.$t('error.fetchFailed');
      } finally {
        this.loading = false;
      }
    },
    async clearLogs() {
      try {
        this.loading = true;
        const response = await axios.post(ajaxurl, new URLSearchParams({
          action: 'sms_ir_clear_error_logs',
        }));

        if (response.data.success) {
          this.errorLogs = [];
          this.$toast.success(this.$t('reports.logsCleared'));
        } else {
          this.$toast.error(this.$t('error.clearFailed'));
        }
      } catch (err) {
        console.error('Error clearing logs:', err);
        this.$toast.error(err.response?.data?.message || this.$t('error.clearFailed'));
      } finally {
        this.loading = false;
      }
    },
    formatNumber(num) {
      if (num === null || num === undefined) return "0";
      if (typeof num === 'number' && !Number.isInteger(num)) {
        num = Math.floor(num);
      }
      return num.toLocaleString("fa-IR");
    },
    getDeliveryStatus(state) {
      const states = {
        1: this.$t('reports.deliveryStatus.deliveredToPhone'),      // رسیده به گوشی
        2: this.$t('reports.deliveryStatus.notDeliveredToPhone'),   // نرسیده به گوشی
        3: this.$t('reports.deliveryStatus.processingByTelecom'),   // پردازش در مخابرات
        4: this.$t('reports.deliveryStatus.notDeliveredToTelecom'), // نرسیده به مخابرات
        5: this.$t('reports.deliveryStatus.deliveredToTelecom'),    // رسیده به مخابرات
        6: this.$t('reports.deliveryStatus.error'),                 // خطا
        7: this.$t('reports.deliveryStatus.blacklist')              // لیست سیاه
      }
      return states[state] || this.$t('reports.deliveryStatus.error')
    },
    formatDate(timestamp) {
      if (!timestamp) return '';
      // Convert Unix timestamp to milliseconds for JavaScript Date
      const date = new Date(timestamp * 1000);
      return date.toLocaleString('fa-IR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    },
    getMethod(context) {
      if (!context || typeof context !== 'object') return '';
      return context.endpoint || '';
    },
    getStatusCode(context) {
      if (!context || typeof context !== 'object') return '';
      if (context.response && context.response.status) {
        return context.response.status;
      }
      return '';
    },
  getContextMessage(context) {
  if (!context || typeof context !== 'object') return '';
    
  if (context.response) {
    if (context.response.error) {
      return context.response.error;
    }
  }
  if (context.error) {
      return context.error;
    }

  return JSON.stringify(context, null, 2);
}
  },
  mounted() {
    // Set initial tab from URL
    this.currentTab = this.getTabFromUrl();
    this.fetchData();
  }
};
</script>

<style scoped>
.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #0D0D0D;
}

select {
  background-position: left 0.5rem center;
}
</style>
